import { useEffect, useRef, useState } from "react";
import { pageSize } from "../../api/apiUtils";
import {
  getDefaultSensorsSearchQuery,
  SensorSearchQueryType,
} from "../common/QueryTypes";
import { SensorCreateDto, SensorResponseDto } from "../common/ObjectTypes";
import { useProfile } from "../common/ProfileProvider";
import { useTranslation } from "react-i18next";
import { createSensor, getSensors, updateSensor } from "../../api/sensorsApi";
import SensorsList from "../sensors/SensorsList";
import SensorForm from "../sensors/SensorForm";
import { toast } from "react-toastify";
import SearchBarSensors from "../sensors/SearchBarSensors";
import { maxPurchasePrice } from "../../constants";
import tailwindClassnames from "../common/TailwindClassnames";
import { useNavigate } from "react-router-dom";
import ImportFromCsv from "../csvImport/ImportFromCsv";
import { canMigrateFullExport } from "../csvImport/csvImport";
import FollowImportUntilSettled from "../csvImport/FollowImportUntilSettled";
import type { CsvImportJobDto } from "../../api/migrationsApi";

type OrganizationSensorTabProps = {
  orgId: number;
  /** The organisation's guid, for the New Project deep link a full WI migration lands on. */
  orgGuid: string;
  perms: any[];
};

const OrganizationSensorsTab: React.FC<OrganizationSensorTabProps> = ({
  orgId,
  orgGuid,
  perms,
}) => {
  const [loading, setLoading] = useState(true);
  const initialSensorsSearchQuery = getDefaultSensorsSearchQuery(orgId);
  const [sensorsSearchQuery, setSensorsSearchQuery] =
    useState<SensorSearchQueryType>(initialSensorsSearchQuery);
  const [sensors, setSensors] = useState<SensorResponseDto[]>([]);
  const [sensorLoading, setSensorLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const [existingSensor, setExistingSensor] = useState(false);
  const emptySensor: SensorCreateDto = {
    org_id: orgId,
    sensor_name: "",
    sensor_id: undefined,
    sensor_type: "camera",
    make: null,
    model: null,
    serial_number: null,
    purchase_date: null,
    purchase_price: null,
    product_url: null,
    comments: null,
    is_active: true,
    decommission_reason: null,
  };
  const [sensor, setSensor] = useState<SensorCreateDto>(emptySensor);
  const [originalSensor, setOriginalSensor] =
    useState<SensorResponseDto | null>(null);
  const [totalSensors, setTotalSensors] = useState(0);
  const [canAddSensor, setCanAddSensor] = useState(false);
  const [canEditSensor, setCanEditSensor] = useState(false);
  const [sensorModalOpen, setSensorModalOpen] = useState(false);
  const { profile, profileLoading } = useProfile();
  const { t } = useTranslation();
  const [modalSaving, setModalSaving] = useState(false);
  const [savingAndAddAnother, setSavingAndAddAnother] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // Imports the dialog let go of while the server was still applying them. The dialog's
  // own poller stops with the dialog, so the tab follows each one until it settles and
  // then refreshes the list — otherwise an apply that finishes after the close leaves the
  // list stale (PR #761 review). A list, not a single slot: letting go of a second job must
  // not stop following the first.
  const [pendingImportGuids, setPendingImportGuids] = useState<string[]>([]);
  const navigate = useNavigate();
  // Whether the CSV import's Wildlife Insights fork may offer the full migration. Decided
  // here, from the same permissions the Projects tab uses, because the deep link it lands on
  // opens the WI modal for anyone — see `canMigrateFullExport`.
  const offerFullMigration = canMigrateFullExport(perms, !!profile?.is_admin);

  /*
   * Set permissions for adding a new sensor.
   */
  useEffect(() => {
    if (!profileLoading && profile) {
      const isAdmin = profile?.is_admin;
      const hasPermissionToModifySensors = perms.some((perm: any) =>
        perm.name.includes("EDIT_SENSORS_IN_ORG"),
      );
      const hasPermissionsToAddSensors = perms.some((perm: any) =>
        perm.name.includes("CREATE_SENSORS_IN_ORG"),
      );
      setCanAddSensor(isAdmin || hasPermissionsToAddSensors);
      setCanEditSensor(isAdmin || hasPermissionToModifySensors);
    }
  }, [profileLoading, profile, perms]);

  // Ordinal of the most recent sensors request. Refreshes can overlap — two import
  // followers settling close together, or a page change while a refresh is in flight — and
  // responses need not come back in order, so only the newest request may write the list;
  // an older response arriving late is dropped (PR #763 review).
  const latestSensorsRequest = useRef(0);

  /*
   * Fetch sensors from the API. Only the most recently issued request updates the list.
   */
  const fetchSensors = async () => {
    const requestId = ++latestSensorsRequest.current;
    setLoading(true);
    try {
      const temp = await getSensors(sensorsSearchQuery);
      if (requestId !== latestSensorsRequest.current) return;
      setSensors(temp.sensors);
      setTotalSensors(temp.count);
    } catch (err) {
      if (requestId !== latestSensorsRequest.current) return;
      console.error("Error loading sensors:", err);
    } finally {
      // A superseded request must not clear the loading state the newer one still owns.
      if (requestId === latestSensorsRequest.current) setLoading(false);
    }
  };

  /*
   * A followed import has settled (or could not be checked on any more): stop following it
   * and reload the list once. `FollowImportUntilSettled` calls the latest copy of this, so
   * the refetch uses the current search query.
   */
  const handleImportSettled = (guid: string) => {
    setPendingImportGuids((current) => current.filter((g) => g !== guid));
    fetchSensors();
  };

  /*
   * Load sensors when query skip changes.
   */
  useEffect(() => {
    const loadSensors = async () => {
      await fetchSensors();
    };
    loadSensors();
  }, [
    orgId,
    sensorsSearchQuery.skip,
    sensorsSearchQuery.sort_by,
    sensorsSearchQuery.sort_direction,
    sensorsSearchQuery.purchase_date_end,
    sensorsSearchQuery.active_only,
  ]);

  const onAddSensor = () => {
    openSensorModal(true);
  };

  /*
   * Opens sensor modal.
   */
  const openSensorModal = (createNewSensor: boolean) => {
    setExistingSensor(!createNewSensor);
    if (createNewSensor) {
      setSensor(emptySensor);
      setOriginalSensor(null);
    }
    setSensorModalOpen(true);
  };

  /*
   * Closes sensor modal.
   */
  const closeSensorModal = () => {
    setSensorModalOpen(false);
    setErrors({});
  };

  /*
   * Opens the CSV import (AKL-813). The Add Sensor modal is closed first: both overlays sit
   * at z-50 inside this tab's fixed wrapper, so leaving it open would stack two dialogs.
   */
  const openImportModal = () => {
    setSensorModalOpen(false);
    setErrors({});
    setImportOpen(true);
  };

  /*
   * An import was applied: reload the list. Called directly rather than through a
   * `skip: 0` update, which does not re-run the load effect when skip is already 0.
   */
  const handleImported = (_job: CsvImportJobDto) => {
    fetchSensors();
  };

  /*
   * The dialog is letting go of a job — on close, or when the user picks another file after
   * polling gave up. A job the server was still applying is followed from here until it
   * settles (see `pendingImportGuids`); nothing else needs following: a dry run writes
   * nothing, and a job already applied refreshed the list through `handleImported`.
   */
  const followIfStillApplying = (job: CsvImportJobDto | null) => {
    if (job?.status === "applying") {
      setPendingImportGuids((current) =>
        current.includes(job.guid) ? current : [...current, job.guid],
      );
    }
  };

  /*
   * The import dialog closed. A closed dialog cannot be reopened to the same job — there is
   * no organisation-level import history in AKL-813 — so an apply still running is followed
   * from here. A job that had already been applied needs nothing: `handleImported`
   * refreshed the list the moment it reached that state, so refetching again on Done would
   * only repeat the request.
   */
  const handleImportClose = (lastJob: CsvImportJobDto | null) => {
    setImportOpen(false);
    followIfStillApplying(lastJob);
  };

  /*
   * The user chose a full Wildlife Insights migration instead: hand off to the New Project
   * form, which opens the migration modal from the `?migrate=wi` deep link (AKL-812).
   */
  const handleMigrateFullExport = () => {
    setImportOpen(false);
    if (!orgGuid) {
      console.error("Organization GUID is missing");
      return;
    }
    navigate(`/organizations/${orgGuid}/addProject?migrate=wi`);
  };

  /*
   * Handle sensor form change.
   * @param e: event object.
   */
  const handleSensorFormChange = (e: any) => {
    let { name, value } = e.target;
    if (value === "") value = null;
    setSensor((prevSensor) => ({
      ...prevSensor,
      [name]: value,
    }));
  };

  /*
   * Handle selecting a sensor in the sensor list.
   * @param e: event object.
   */
  const handleSelectSensor = (sensor: SensorResponseDto) => {
    setSensorLoading(true);
    openSensorModal(false);
    if (sensor.purchase_price) {
      sensor.purchase_price = Number(sensor.purchase_price).toFixed(2);
    }
    try {
      setSensor(sensor);
      setOriginalSensor(sensor);
      setSensorLoading(false);
    } catch (error) {
      console.error("Error selecting sensor:", error);
      closeSensorModal();
    }
  };

  /*
   * Handle page click event.
   * @param e: event object.
   */
  const handlePageClick = (e: any) => {
    const selectedPage = e.selected;
    const skip = Math.ceil(
      selectedPage * (sensorsSearchQuery.take || pageSize),
    );
    setSensorsSearchQuery((prevQuery) => ({
      ...prevQuery,
      skip: skip,
    }));
  };

  /*
   * Request sort of sensors.
   * @param sortby: column to sort by.
   * @param sortdirection: direction to sort by (ascending or descending).
   * @returns void.
   *
   */
  const requestSort = (sortby: string, sortdirection: "asc" | "desc") => {
    setSensorsSearchQuery((prevQuery) => ({
      ...prevQuery,
      sort_by: sortby,
      sort_direction: sortdirection,
      skip: 0,
    }));
  };

  /*
   * Checks if sensor form data is valid.
   * @returns boolean.
   */
  const formIsValid = () => {
    let form_errors: any = {};
    if (sensor.sensor_name.length === 0)
      form_errors.sensor_name = t("Name is required");

    if (sensor.sensor_type === null || sensor.sensor_type === "")
      form_errors.sensor_type = t("Type is required");

    // validate purchase price has 2 decimal places
    if (sensor.purchase_price) {
      const purchasePrice = parseFloat(sensor.purchase_price);
      if (isNaN(purchasePrice) || purchasePrice < 0)
        form_errors.purchase_price = t(
          "Purchase price must be a positive number",
        );
      else {
        if (!/^\d+(\.\d{2})?$/.test(purchasePrice.toString()))
          form_errors.purchase_price = t(
            "Purchase price must have 2 decimal places",
          );
      }
      if (purchasePrice > maxPurchasePrice) {
        form_errors.purchase_price =
          t("Purchase price cannot be greater than") + " " + maxPurchasePrice;
      }
    }

    setErrors(form_errors);
    return Object.keys(form_errors).length === 0;
  };

  /*
   * Handle saving form data event to create new sensor or update existing sensor data.
   * @param addAnother: whether to add another sensor or not.
   */
  const handleSave = async () => {
    if (!formIsValid()) return;
    if (!sensor.org_id || sensor.org_id === 0) {
      toast.error(t("Error with sensor data"));
      return;
    }
    if (existingSensor) {
      if (!sensor.sensor_id || sensor.sensor_id === 0) {
        toast.error(t("Error updating sensor"));
        return;
      }
      try {
        setSaving(true);
        await updateSensor(sensor);
        toast.success(t("Sensor updated"));
        fetchSensors();
        closeSensorModal();
      } catch (error: any) {
        toast.error(
          t("Error updating sensor") + ": " + error.data?.detail?.toString(),
        );
      }
      setSaving(false);
    } else {
      try {
        setSaving(true);
        await createSensor(sensor);
        toast.success(t("Sensor added"));
        setSaving(false);
        closeSensorModal();
        fetchSensors();
      } catch (error: any) {
        toast.error(
          t("Error adding sensor") + ": " + error.data.detail.toString(),
        );
        setSaving(false);
      }
    }
  };

  /*
   * Handle saving form data event to create new sensor or update existing sensor data and add another sensor.
   */
  const handleSaveAndAddAnother = async () => {
    if (!formIsValid()) return;
    if (!sensor.org_id || sensor.org_id === 0) {
      toast.error(t("Error with sensor data"));
      return;
    }
    try {
      setSavingAndAddAnother(true);
      await createSensor(sensor);
      toast.success(t("Sensor added"));
      setSensor(emptySensor);
      setOriginalSensor(null);
      setSavingAndAddAnother(false);
      fetchSensors();
    } catch (error: any) {
      toast.error(
        t("Error adding sensor") + ": " + error.data.detail.toString(),
      );
      setSavingAndAddAnother(false);
      closeSensorModal();
    }
  };

  /*
   * Update the search query and reset skip if needed.
   */
  const handleSearchQueryChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    setSensorsSearchQuery((prevQuery) => ({
      ...prevQuery,
      [e.target.name]: e.target.value,
    }));

    // reset skip to 0 when start date changes, trigger fetch sensors
    if (
      e.target.name === "purchase_date_start" &&
      e.target.value !== sensorsSearchQuery.purchase_date_start
    ) {
      setSensorsSearchQuery((prevQuery) => ({
        ...prevQuery,
        skip: 0,
      }));
    }
  };

  /*
   * Fetch sensors when search query changes.
   */
  const handleSearch = async () => {
    if (sensorsSearchQuery.skip !== 0) {
      // reset skip to 0 when searching, which will trigger fetchSensors
      setSensorsSearchQuery((prevQuery) => ({
        ...prevQuery,
        skip: 0,
      }));
    } else {
      await fetchSensors();
    }
  };

  /*
   * Handle decommissioning sensor event.
   */
  const handleDecommissionSensor = async () => {
    try {
      setModalSaving(true);
      await updateSensor({
        ...sensor,
        is_active: false,
      });
      toast.success(t("Sensor decommissioned"));
      setModalSaving(false);
      closeSensorModal();
      fetchSensors();
    } catch (error: any) {
      toast.error(
        t("Error decommissioning sensor") + ": " + error.data.detail.toString(),
      );
    }
  };

  /*
   * Handle reactivating sensor event
   */
  const handleReactivateSensor = async () => {
    try {
      setModalSaving(true);
      await updateSensor({
        ...sensor,
        is_active: true,
        decommission_reason: null,
      });
      toast.success(t("Sensor reactivated"));
      setModalSaving(false);
      closeSensorModal();
      fetchSensors();
    } catch (error: any) {
      toast.error(
        t("Error reactivating sensor") + ": " + error.data.detail.toString(),
      );
    }
  };

  return (
    <div className="flex flex-row w-full">
      <div className="fixed z-50">
        {sensorModalOpen && (
          <SensorForm
            sensor={sensor}
            sensorLoading={sensorLoading}
            onClose={() => closeSensorModal()}
            onChange={handleSensorFormChange}
            onSave={handleSave}
            onSaveAndAddAnother={handleSaveAndAddAnother}
            errors={errors}
            saving={saving}
            canEdit={canEditSensor}
            existingSensor={existingSensor}
            onDecommissionSensorConfirmation={handleDecommissionSensor}
            onReactiveSensorConfirmation={handleReactivateSensor}
            modalSaving={modalSaving}
            savingAndAddAnother={savingAndAddAnother}
            saveDisabled={
              JSON.stringify(originalSensor) === JSON.stringify(sensor)
            }
            onImportFromCsv={canAddSensor ? openImportModal : undefined}
          />
        )}
        {/* Never mounted without CREATE_SENSORS_IN_ORG: the API's 403 on the upload
            would be a dead end for the user (no longer a sign-out since AKL-939, but
            still a bundle chosen for nothing). */}
        {canAddSensor && importOpen && (
          <ImportFromCsv
            isOpen={importOpen}
            orgId={orgId}
            entity="sensors"
            formats={["akili_csv", "ctms_csv"]}
            canMigrateFullExport={offerFullMigration}
            onClose={handleImportClose}
            onImported={handleImported}
            onMigrateFullExport={handleMigrateFullExport}
            onJobAbandoned={followIfStillApplying}
          />
        )}
        {/* One follower per import still applying after its dialog let go of it; each has
            its own poller, so a second one never cancels the first. Render nothing visible. */}
        {pendingImportGuids.map((guid) => (
          <FollowImportUntilSettled
            key={guid}
            guid={guid}
            onSettled={handleImportSettled}
          />
        ))}
      </div>
      <div className="overflow-x-auto w-full mx-8 mt-8">
        {/* The toolbar (SearchBarSensors) is card-interior — it must be the
            card's first child so its bottom hairline attaches to the table,
            mirroring OrganizationProjectsTab's structure. */}
        <div className={tailwindClassnames.tableCard}>
          <SearchBarSensors
            canAddSensor={canAddSensor}
            onAddSensor={onAddSensor}
            onChangeSearchQuery={handleSearchQueryChange}
            onSearch={handleSearch}
            sensorSearchQuery={sensorsSearchQuery}
            onImportFromCsv={canAddSensor ? openImportModal : undefined}
          />
          <SensorsList
            sensors={sensors}
            totalCount={totalSensors}
            take={pageSize}
            offset={sensorsSearchQuery.skip ?? 0}
            loading={loading}
            onSelectedSensor={handleSelectSensor}
            onPageClick={handlePageClick}
            requestSort={requestSort}
            canEditSensor={canEditSensor}
          />
        </div>
      </div>
    </div>
  );
};

export default OrganizationSensorsTab;
